import FreightCalculationDisplayModelView from "../QuotePricePage/FreightCalculationDisplayModelView";

import { useState, useEffect, useCallback, useContext } from "react";
import {
    Container,
    Paper,
    Select,
    MenuItem,
    TextField,
    Button,
    Grid,
    Typography,
    Box,
    FormControl,
    InputLabel,
    CircularProgress,
    Snackbar,
    Alert,
    Fade,
    Card,
    CardContent,
    Divider,
    Stepper,
    Step,
    StepLabel,
} from "@mui/material";
import ForgeViewer from "../designToolPage/forgeViewer";
import apiClient from "../../Helpers/apiClient";
import { useLocation, useNavigate } from "react-router-dom";
import AuthContext from "../../Helpers/auth-context";
import Dynamics365NavigationBar from "./Dynamics365NavigationBar";
import useAutoLogout from "../../Helpers/useAutoLogout";
import { useQuote } from "./useQuote";
import BackToCPQButton from "../../Components/backToCPQButton";
import { useSignalR } from "../../Contexts/SignalRContext";
import { useEcoProtectorState, WORKFLOW_STAGES } from "../../Hooks/useEcoProtectorState";
import AddressAutocomplete from "../../Components/AddressAutocomplete";

const STAGE_LABELS = ["Calculate Price", "Generate Model", "Download & Quote"];

const PriceBreakdown = ({ priceData }) => {
    if (!priceData) return null;

    const items = [
        { label: "Tank Base", value: priceData.tankPrice },
        { label: "Riser", value: priceData.riserPrice },
        { label: "Inlet Connection", value: priceData.inletPrice },
        { label: "Outlet Connection", value: priceData.outletPrice },
        { label: "Cover", value: priceData.coverPrice },
        { label: "Freight", value: priceData.freightPrice },
        { label: "Total Price", value: priceData.totalPrice },
    ];

    return (
        <Card sx={{ mt: 2 }}>
            <CardContent>
                <Typography variant="h6" gutterBottom>
                    Price Breakdown
                </Typography>
                {items.map(({ label, value }) => (
                    <Box
                        key={label}
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            mb: 1,
                        }}
                    >
                        <Typography variant="body2">{label}:</Typography>
                        <Typography variant="body2">${value?.toFixed(2) || "0.00"}</Typography>
                    </Box>
                ))}
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="h6" color="primary">
                        Total:
                    </Typography>
                    <Typography variant="h6" color="primary">
                        ${priceData.totalPrice?.toFixed(2) || "0.00"}
                    </Typography>
                </Box>
            </CardContent>
        </Card>
    );
};

const DownloadButtons = ({ urls, onDownload, disabled }) => (
    <Box>
        {Object.entries(urls).map(([type, url]) => (
            <Button
                key={type}
                fullWidth
                variant="outlined"
                onClick={() => onDownload(url, type)}
                disabled={disabled || !url}
                sx={{ mt: 1 }}
            >
                Download {type.toUpperCase()}
            </Button>
        ))}
    </Box>
);

const Dynamics365EcoProtector = () => {
    const [freightCalculationResult, setFreightCalculationResult] = useState(null);

    const { state } = useContext(AuthContext);
    const { isConnected: isSignalRConnected, connectionId } = useSignalR();
    const location = useLocation();
    const navigate = useNavigate();
    const { addProduct } = useQuote();

    // Local state
    const [QuoteId, setQuoteId] = useState("");
    const [quantity, setQuantity] = useState(1);
    const [isCalculatingPrice, setIsCalculatingPrice] = useState(false);
    const [isAddingToQuote, setIsAddingToQuote] = useState(false);
    const [coverHeight, setCoverHeight] = useState(100);
    const [connectionSizes, setConnectionSizes] = useState([]);
    const [connectionTypes, setConnectionTypes] = useState([]);
    const [tankInvertStandard, setTankInvertStandard] = useState(0);
    const [errors, setErrors] = useState({ fslAndInletIL: "", riser: "" });
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    const showNotification = useCallback((message, severity = "success") => {
        setSnackbar({ open: true, message, severity });
    }, []);

    // Use the enhanced state hook
    const {
        currentStage,
        activeStep,
        isGenerating,
        totalPrice,
        priceBreakdown,
        viewerPath,
        downloadUrls,
        modelConfig,
        setIsGenerating,
        setTotalPrice,
        setPriceBreakdown,
        setModelConfig,
        moveToGenerateStage,
        resetWorkflow,
        restoreStateFromLocalStorage,
        saveStateToLocalStorage,
        isSignalRConnected: stateSignalRConnected,
        signalRConnectionId,
    } = useEcoProtectorState(QuoteId, quantity, showNotification);

    // Constants
    const modelTypes = ["ECP.1200", "ECP.1500", "ECP.1850", "ECP.2200", "ECP.2500", "ECP.3000", "ECP.3500"];
    // const freightLocations = ["NT", "SA", "WA", "NSW", "QLD", "TAS", "VIC"];
    const coverTypes = ["Class B", "Class D", "Class E"];

    useAutoLogout({ isAuthenticated: state.isAuthenticated });

    // Helper functions for workflow control
    const isStage = (stage) => currentStage === stage;
    const canInteractWithInputs = () => isStage(WORKFLOW_STAGES.CALCULATE_PRICE) && !isCalculatingPrice;
    const canCalculatePrice = () => isStage(WORKFLOW_STAGES.CALCULATE_PRICE) && !isCalculatingPrice;
    const canGenerateModel = () => isStage(WORKFLOW_STAGES.GENERATE_MODEL) && !isGenerating && totalPrice > 0;
    const canDownloadAndQuote = () => isStage(WORKFLOW_STAGES.DOWNLOAD_AND_QUOTE);

    // Prevent spacebar scrolling
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.code === "Space" && !["INPUT", "TEXTAREA"].includes(document.activeElement.tagName)) {
                event.preventDefault();
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, []);

    // Initialize and check auth
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const id = params.get("id");

        console.log("🔧 EcoProtector: Initializing with QuoteId from URL:", id);
        if (id) {
            setQuoteId(id);
        }

        if (!state.isAuthenticated) {
            navigate("/login", {
                state: { returnTo: location.pathname + location.search },
            });
        } else {
            // Restore state when component mounts
            const restored = restoreStateFromLocalStorage();
            if (restored) {
                showNotification("Previous session restored", "info");
            }
        }
    }, [location, state.isAuthenticated, navigate, restoreStateFromLocalStorage, showNotification]);

    // Show SignalR connection status
    useEffect(() => {
        if (isSignalRConnected) {
            console.log("SignalR connected in EcoProtector");
        } else {
            console.log("SignalR disconnected in EcoProtector");
        }
    }, [isSignalRConnected]);

    const handleFreightPriceOverride = (newFreightPrice) => {
        setPriceBreakdown((prev) => ({
            ...prev,
            freightPrice: newFreightPrice,
            totalPrice: prev.totalPrice - prev.freightPrice + newFreightPrice,
        }));
    };

    // Load connection sizes
    const loadConnectionSizes = useCallback(
        async (modelType) => {
            try {
                const response = await apiClient.post("/api/model/connectionsizes", modelType, {
                    headers: { "Content-Type": "application/json" },
                });

                if (response.data?.item1) {
                    setConnectionSizes(response.data.item1);
                    setTankInvertStandard(response.data.item2);

                    if (response.data.item1.length > 0 && !modelConfig.connectionSize) {
                        const firstSize = response.data.item1[0];
                        setModelConfig((prev) => ({
                            ...prev,
                            connectionSize: firstSize,
                        }));
                        await loadConnectionTypes(firstSize);
                    }
                }
            } catch (err) {
                showNotification("Failed to load connection sizes", "error");
            }
        },
        [modelConfig.connectionSize, setModelConfig, showNotification]
    );

    // Load connection types
    const loadConnectionTypes = useCallback(
        async (connectionSize) => {
            try {
                const response = await apiClient.post("/api/model/connectiontypes", connectionSize, {
                    headers: { "Content-Type": "application/json" },
                });

                if (response.data && Array.isArray(response.data)) {
                    setConnectionTypes(response.data);
                    if (response.data.length > 0 && !modelConfig.connectionType) {
                        setModelConfig((prev) => ({
                            ...prev,
                            connectionType: response.data[0],
                        }));
                    }
                }
            } catch (err) {
                showNotification("Failed to load connection types", "error");
            }
        },
        [modelConfig.connectionType, setModelConfig, showNotification]
    );

    // Calculate price
    const calculatePrice = useCallback(async () => {
        const { finishSurfaceLevel, inletIL, connectionSize, connectionType, coverType, city, modelType } =
            modelConfig;

        let newErrors = { fslAndInletIL: "", riser: "" };

        if (isNaN(finishSurfaceLevel) || isNaN(inletIL)) {
            newErrors.fslAndInletIL = "Finish Surface Level and Inlet Invert Levels are required";
        } else if ((finishSurfaceLevel - inletIL).toFixed(2) < tankInvertStandard) {
            newErrors.fslAndInletIL = `Inlet IL to the FSL must be equal to or greater than the Tank Invert Standard(${tankInvertStandard})`;
        }

        if (!connectionSize || !connectionType) {
            newErrors.fslAndInletIL = "Please select connection size and type";
        }

        setErrors(newErrors);
        if (newErrors.fslAndInletIL) return;

        setIsCalculatingPrice(true);

        try {
            const data = {
                Model: modelType,
                FinishSurfaceLevel: finishSurfaceLevel,
                InletIL: inletIL,
                InletSize: parseInt(connectionSize),
                OutletSize: parseInt(connectionSize),
                ConnectionType: connectionType,
                CoverType: coverType,
                FreightLocation: city,
            };

            const response = await apiClient.post("/api/price/calculation", data, {
                headers: { "Content-Type": "application/json" },
            });

            if (response.data) {
                setTotalPrice(response.data.totalPrice);
                setCoverHeight(response.data.coverHeight);
                setPriceBreakdown(response.data);
                setFreightCalculationResult(response.data.freightResult || null);

                const riser = parseFloat(response.data.riser) * 1000;
                const riserHeight = riser < 100 ? 100 : riser;
                const riserError = riser < 100 ? "Riser height should be minimum 100mm" : "";

                setModelConfig((prev) => ({ ...prev, riserHeight }));
                setErrors((prev) => ({ ...prev, riser: riserError }));

                showNotification("Price calculated successfully! You can now generate the model.");
                moveToGenerateStage();
            }
        } catch (err) {
            showNotification("Failed to calculate price", "error");
        } finally {
            setIsCalculatingPrice(false);
        }
    }, [
        modelConfig,
        tankInvertStandard,
        showNotification,
        moveToGenerateStage,
        setTotalPrice,
        setPriceBreakdown,
        setModelConfig,
    ]);

    // Handlers
    const handleModelTypeChange = useCallback(
        async (event) => {
            const value = event.target.value;
            setModelConfig((prev) => ({ ...prev, modelType: value }));
            await loadConnectionSizes(value);
        },
        [loadConnectionSizes, setModelConfig]
    );

    const handleConnectionSizeChange = useCallback(
        async (event) => {
            const value = event.target.value;
            setModelConfig((prev) => ({ ...prev, connectionSize: value }));
            await loadConnectionTypes(value);
        },
        [loadConnectionTypes, setModelConfig]
    );

    const handleConfigChange = useCallback(
        (updates) => {
            setModelConfig((prev) => ({ ...prev, ...updates }));
        },
        [setModelConfig]
    );

    const handleGenerate = useCallback(async () => {
        if (!connectionId) {
            showNotification("SignalR connection not established", "error");
            return;
        }

        setIsGenerating(true);
        saveStateToLocalStorage(); // Save state when starting generation

        const diameter = parseInt(modelConfig.modelType.split(".")[1]);

        const data = {
            browserConnectionId: connectionId,
            params: {
                Diameter: diameter.toString(),
                FSL: (modelConfig.finishSurfaceLevel * 100).toString(),
                InvertIL: (modelConfig.inletIL * 100).toString(),
                InletDiameter: modelConfig.connectionSize || "150",
                InletType: "RCP",
                LidThickness: coverHeight.toString(),
            },
        };

        try {
            const response = await apiClient.post("/api/aps/designautomation/workitems", data);
            if (response.status !== 200) throw new Error("Failed to generate model");

            showNotification(
                "Model generation started. You can navigate away and come back later to see the results.",
                "info"
            );
        } catch (err) {
            setIsGenerating(false);
            showNotification("Failed to generate model", "error");
        }
    }, [connectionId, modelConfig, coverHeight, showNotification, saveStateToLocalStorage, setIsGenerating]);

    const handleDownload = useCallback(
        async (url, fileType) => {
            try {
                const workitemId = url.split("Workitems/")[1].split("/")[0];
                const response = await apiClient.get(`/api/aps/designautomation/download/${fileType}`, {
                    params: { workitemId },
                    responseType: "blob",
                    headers: {
                        "Content-Type": "application/octet-stream",
                        Authorization: `Bearer ${localStorage.getItem("jwtToken")}`,
                    },
                });

                const extensions = {
                    pdf: ".pdf",
                    dwg: ".dwg",
                    dwg3d: "._3D.dwg",
                };
                const filename = `1200 Compete${extensions[fileType] || `.${fileType}`}`;

                const blob = new Blob([response.data], {
                    type: response.headers["content-type"] || "application/octet-stream",
                });

                const downloadUrl = window.URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = downloadUrl;
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(downloadUrl);

                showNotification("File downloaded successfully");
            } catch (err) {
                showNotification(`Error downloading file: ${err.message}`, "error");
            }
        },
        [showNotification]
    );

    // Add to CPQ quote
    const addToCPQQuote = async () => {
        setIsAddingToQuote(true);

        if (!totalPrice || totalPrice <= 0) {
            showNotification("Please calculate price first", "error");
            setIsAddingToQuote(false);
            return;
        }

        try {
            const detailDescription = [
                `Model: ${modelConfig.modelType}`,
                `Finish Surface Level: ${modelConfig.finishSurfaceLevel}m`,
                `Inlet IL: ${modelConfig.inletIL}m`,
                `Cover Type: ${modelConfig.coverType}`,
                `Freight Location: ${modelConfig.city}`,
                `Riser Height: ${modelConfig.riserHeight}mm`,
                modelConfig.connectionSize && `Connection Size: ${modelConfig.connectionSize}mm`,
                modelConfig.connectionType && `Connection Type: ${modelConfig.connectionType}`,
                modelConfig.workItemId && `Work Item ID: ${modelConfig.workItemId}`,
            ]
                .filter(Boolean)
                .join(" | ");

            const productData = {
                workItemId: modelConfig.workItemId || null,
                quoteId: QuoteId || null,
                productType: "EcoProtector",
                modelConfig: {
                    ...modelConfig,
                    configurationSummary: `${modelConfig.modelType} - ${modelConfig.coverType} - ${modelConfig.city}`,
                },
                priceBreakdown: priceBreakdown ? { ...priceBreakdown } : null,
                totalPrice: totalPrice,
                productName: `EcoProtector ${modelConfig.modelType}`,
                partNumber: modelConfig.modelType,
                pcs_DetailDescriptions: detailDescription,
                quantity: quantity,
                price: totalPrice,
            };

            addProduct(productData);
            showNotification(`EcoProtector ${modelConfig.modelType} added to CPQ quote!`, "success");
            setTimeout(() => {
                resetWorkflow();
                setIsAddingToQuote(false); // Add this line
            }, 1500);
        } catch (error) {
            showNotification(`Failed to add product: ${error.message}`, "error");
            setIsAddingToQuote(false);
        }
    };

    // Initialize connection sizes
    useEffect(() => {
        loadConnectionSizes(modelConfig.modelType);
    }, [loadConnectionSizes, modelConfig.modelType]);

    return (
        <Container>
            <Dynamics365NavigationBar />

            <Paper sx={{ p: 2, mb: 3 }}>
                <Stepper activeStep={activeStep} alternativeLabel>
                    {STAGE_LABELS.map((label, index) => (
                        <Step key={label} completed={activeStep > index}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                {/* Status indicators */}
                <Box sx={{ mt: 2, display: "flex", gap: 2, justifyContent: "center", alignItems: "center" }}>
                    <Typography variant="caption" color={isSignalRConnected ? "success.main" : "error.main"}>
                        SignalR: {isSignalRConnected ? "Connected" : "Disconnected"}
                    </Typography>
                    {isGenerating && (
                        <Typography variant="caption" color="info.main">
                            Model generation in progress...
                        </Typography>
                    )}
                </Box>

                {currentStage !== WORKFLOW_STAGES.CALCULATE_PRICE && (
                    <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={resetWorkflow}
                            disabled={isGenerating || isCalculatingPrice || isAddingToQuote}
                        >
                            Start New Configuration
                        </Button>
                    </Box>
                )}
            </Paper>

            <Grid container spacing={2}>
                <Grid item xs={4}>
                    <Paper
                        sx={{
                            p: 3,
                            mt: 3,
                            elevation: 4,
                            border: "1px solid #ddd",
                            boxShadow: 1,
                        }}
                    >
                        <Fade in={true}>
                            <Box>
                                <FormControl fullWidth margin="normal">
                                    <InputLabel>Model Type</InputLabel>
                                    <Select
                                        value={modelConfig.modelType}
                                        onChange={handleModelTypeChange}
                                        disabled={!canInteractWithInputs()}
                                    >
                                        {modelTypes.map((type) => (
                                            <MenuItem key={type} value={type}>
                                                {type}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <TextField
                                            fullWidth
                                            margin="normal"
                                            type="number"
                                            label="Finish Surface Level (m)"
                                            value={modelConfig.finishSurfaceLevel}
                                            onChange={(e) =>
                                                handleConfigChange({
                                                    finishSurfaceLevel: parseFloat(e.target.value),
                                                })
                                            }
                                            error={!!errors.fslAndInletIL}
                                            disabled={!canInteractWithInputs()}
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField
                                            fullWidth
                                            margin="normal"
                                            type="number"
                                            label="Inlet IL (m)"
                                            value={modelConfig.inletIL}
                                            onChange={(e) =>
                                                handleConfigChange({
                                                    inletIL: parseFloat(e.target.value),
                                                })
                                            }
                                            error={!!errors.fslAndInletIL}
                                            disabled={!canInteractWithInputs()}
                                        />
                                    </Grid>
                                </Grid>

                                {errors.fslAndInletIL && (
                                    <Typography
                                        variant="caption"
                                        color="error"
                                        sx={{ mt: 1, display: "block" }}
                                    >
                                        {errors.fslAndInletIL}
                                    </Typography>
                                )}

                                <FormControl fullWidth margin="normal">
                                    <InputLabel>Connection Size (mm)</InputLabel>
                                    <Select
                                        value={modelConfig.connectionSize || ""}
                                        onChange={handleConnectionSizeChange}
                                        disabled={connectionSizes.length === 0 || !canInteractWithInputs()}
                                    >
                                        {connectionSizes.length === 0 ? (
                                            <MenuItem disabled>Loading...</MenuItem>
                                        ) : (
                                            connectionSizes.map((size) => (
                                                <MenuItem key={size} value={size}>
                                                    {size}
                                                </MenuItem>
                                            ))
                                        )}
                                    </Select>
                                </FormControl>

                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <FormControl fullWidth margin="normal">
                                            <InputLabel>Connection Type</InputLabel>
                                            <Select
                                                value={modelConfig.connectionType || ""}
                                                onChange={(e) =>
                                                    handleConfigChange({
                                                        connectionType: e.target.value,
                                                    })
                                                }
                                                disabled={
                                                    connectionTypes.length === 0 || !canInteractWithInputs()
                                                }
                                            >
                                                {connectionTypes.length === 0 ? (
                                                    <MenuItem disabled>Loading...</MenuItem>
                                                ) : (
                                                    connectionTypes.map((type) => (
                                                        <MenuItem key={type} value={type}>
                                                            {type}
                                                        </MenuItem>
                                                    ))
                                                )}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <FormControl fullWidth margin="normal">
                                            <InputLabel>Cover Type</InputLabel>
                                            <Select
                                                value={modelConfig.coverType}
                                                onChange={(e) =>
                                                    handleConfigChange({
                                                        coverType: e.target.value,
                                                    })
                                                }
                                                disabled={!canInteractWithInputs()}
                                            >
                                                {coverTypes.map((type) => (
                                                    <MenuItem key={type} value={type}>
                                                        {type}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                </Grid>

                                {/* <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <FormControl fullWidth margin="normal">
                                            <InputLabel>Freight Location</InputLabel>
                                            <Select
                                                value={modelConfig.freightLocationType}
                                                onChange={(e) =>
                                                    handleConfigChange({
                                                        freightLocationType: e.target.value,
                                                    })
                                                }
                                                disabled={!canInteractWithInputs()}
                                            >
                                                {freightLocations.map((location) => (
                                                    <MenuItem key={location} value={location}>
                                                        {location}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField
                                            fullWidth
                                            margin="normal"
                                            type="number"
                                            label="Riser Height (mm)"
                                            value={modelConfig.riserHeight}
                                            InputProps={{ readOnly: true }}
                                            error={!!errors.riser}
                                            helperText={errors.riser}
                                            disabled={!canInteractWithInputs()}
                                        />
                                    </Grid>
                                </Grid> */}

                                <Grid item xs={12}>
                                    <Box sx={{ display: "flex", alignItems: "center" }}>
                                        <Typography variant="body1" color="text.secondary" sx={{}}>
                                            Destination Address
                                        </Typography>
                                    </Box>
                                    <FormControl fullWidth>
                                        <AddressAutocomplete onAddressSelect={setModelConfig} />
                                    </FormControl>
                                </Grid>

                                <Button
                                    fullWidth
                                    variant="outlined"
                                    onClick={calculatePrice}
                                    disabled={!canCalculatePrice()}
                                    sx={{ mt: 2 }}
                                >
                                    {isCalculatingPrice ? <CircularProgress size={24} /> : "Calculate Price"}
                                </Button>
                                <FreightCalculationDisplayModelView
                                    calculationResult={freightCalculationResult}
                                    onFreightPriceOverride={handleFreightPriceOverride}
                                />

                                <PriceBreakdown priceData={priceBreakdown} />

                                <Button
                                    fullWidth
                                    variant="contained"
                                    onClick={handleGenerate}
                                    disabled={!canGenerateModel()}
                                    sx={{
                                        mt: 2,
                                        backgroundColor: "#1976d2",
                                        color: "white",
                                        "&:hover": {
                                            backgroundColor: "#1565c0",
                                        },
                                        "&:disabled": {
                                            backgroundColor: "#e0e0e0",
                                            color: "#9e9e9e",
                                        },
                                    }}
                                >
                                    {isGenerating ? <CircularProgress size={24} /> : "Generate Model"}
                                </Button>

                                <DownloadButtons
                                    urls={downloadUrls}
                                    onDownload={handleDownload}
                                    disabled={!canDownloadAndQuote()}
                                />
                            </Box>
                        </Fade>
                    </Paper>

                    <Paper
                        sx={{
                            p: 3,
                            mt: 3,
                            elevation: 4,
                            border: "1px solid #ddd",
                            boxShadow: 1,
                        }}
                    >
                        <TextField
                            label="Product Quantity"
                            type="number"
                            disabled={!canDownloadAndQuote()}
                            value={quantity}
                            onChange={(e) => {
                                const value = e.target.value;
                                if (value === "" || (/^\d+$/.test(value) && parseInt(value) > 0)) {
                                    setQuantity(value === "" ? 1 : parseInt(value));
                                }
                            }}
                            variant="outlined"
                            size="small"
                            inputProps={{ min: 1, step: 1 }}
                            sx={{ width: 140, marginTop: 3 }}
                            error={quantity < 1}
                            helperText={quantity < 1 ? "Quantity must be at least 1" : ""}
                        />

                        <Button
                            fullWidth
                            variant="contained"
                            color="secondary"
                            onClick={addToCPQQuote}
                            disabled={
                                !canDownloadAndQuote() || isAddingToQuote || !totalPrice || quantity < 1
                            }
                            sx={{ mt: 2 }}
                        >
                            {isAddingToQuote ? (
                                <CircularProgress size={24} />
                            ) : (
                                `Add to CPQ Quote - ${totalPrice?.toLocaleString() || "0"}`
                            )}
                        </Button>
                    </Paper>
                </Grid>

                <Grid item xs={8}>
                    <Paper sx={{ p: 2, height: "85vh" }}>
                        <ForgeViewer url={viewerPath} loading={isGenerating} />
                    </Paper>
                </Grid>
            </Grid>

            <Box sx={{ mt: 3, mb: 2 }}>
                <BackToCPQButton />
            </Box>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default Dynamics365EcoProtector;
